import { useState, useMemo } from 'react';
import {
  useAgentTemplates,
  useSaveAgentTemplate,
  useDeleteAgentTemplate,
  useAgentSkills,
  useSaveAgentSkill,
  useDeleteAgentSkill,
} from '../hooks/useAgentLibrary';
import Modal from '../components/ui/Modal';

const KIND_LABELS = {
  lead: 'Überagent',
  seo: 'SEO',
  sea: 'SEA',
  cro: 'CRO',
  ab: 'A/B-Tests',
  dev: 'Entwickler',
  tracking: 'Tracking',
  reporting: 'Reporting',
  custom: 'Custom',
};

const KIND_COLORS = {
  lead: 'bg-[#0D3B50] text-white',
  seo: 'bg-emerald-100 text-emerald-700',
  sea: 'bg-amber-100 text-amber-700',
  cro: 'bg-fuchsia-100 text-fuchsia-700',
  ab: 'bg-purple-100 text-purple-700',
  dev: 'bg-slate-200 text-slate-700',
  tracking: 'bg-sky-100 text-sky-700',
  reporting: 'bg-violet-100 text-violet-700',
  custom: 'bg-gray-100 text-gray-700',
};

const DEFAULT_MODEL = 'claude-opus-4-7';
const TOOL_PRESETS = ['web', 'read', 'write', 'bash', 'search'];

export default function AgentLibrary() {
  const [tab, setTab] = useState('templates'); // 'templates' | 'skills'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingSkill, setEditingSkill] = useState(null);

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      <div className="mb-5">
        <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50]">Agent Library</h1>
        <p className="text-[13.5px] text-gray-500 mt-1 max-w-2xl">
          Projektunabhängige Bibliothek aller Agenten-Vorlagen und Skills. Hier definierst du, was
          ein Agent kann — die Zuweisung an Kundenprojekte passiert im{' '}
          <a href="/agent-center" className="text-teal underline">Agent Center</a>.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4 border-b border-gray-200">
        <TabBtn active={tab === 'templates'} onClick={() => setTab('templates')}>Agent-Vorlagen</TabBtn>
        <TabBtn active={tab === 'skills'} onClick={() => setTab('skills')}>Skills</TabBtn>
        <div className="ml-auto pb-2">
          {tab === 'templates' ? (
            <button
              onClick={() => setEditingTemplate({})}
              className="bg-[#0D3B50] text-white text-[13px] px-3.5 py-2 rounded-lg hover:opacity-90"
            >
              + Neue Vorlage
            </button>
          ) : (
            <button
              onClick={() => setEditingSkill({})}
              className="bg-[#0D3B50] text-white text-[13px] px-3.5 py-2 rounded-lg hover:opacity-90"
            >
              + Neuer Skill
            </button>
          )}
        </div>
      </div>

      {tab === 'templates' ? (
        <TemplatesList onEdit={setEditingTemplate} />
      ) : (
        <SkillsList onEdit={setEditingSkill} />
      )}

      {editingTemplate && (
        <TemplateEditor
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
        />
      )}
      {editingSkill && (
        <SkillEditor
          skill={editingSkill}
          onClose={() => setEditingSkill(null)}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-[13.5px] font-medium border-b-2 -mb-[1px] ${
        active ? 'border-[#0D3B50] text-[#0D3B50]' : 'border-transparent text-gray-500 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
}

const KIND_ORDER = ['lead', 'seo', 'sea', 'cro', 'ab', 'dev', 'tracking', 'reporting', 'custom'];

function TemplatesList({ onEdit }) {
  const { data, isLoading } = useAgentTemplates();
  const del = useDeleteAgentTemplate();
  const templates = data?.templates || [];

  const groups = useMemo(() => {
    const byKind = {};
    for (const tpl of templates) {
      const k = tpl.kind || 'custom';
      if (!byKind[k]) byKind[k] = { roots: [], childrenByParent: {} };
      if (tpl.parent_template_id) {
        const pid = tpl.parent_template_id;
        (byKind[k].childrenByParent[pid] ||= []).push(tpl);
      } else {
        byKind[k].roots.push(tpl);
      }
    }
    // Orphan-Schutz: falls Child in anderem `kind` als Parent landet (sollte nicht vorkommen),
    // ergänze es in seiner eigenen kind-Gruppe als Root.
    return Object.entries(byKind)
      .sort(([a], [b]) => KIND_ORDER.indexOf(a) - KIND_ORDER.indexOf(b))
      .map(([kind, g]) => ({ kind, ...g }));
  }, [templates]);

  if (isLoading) return <div className="text-[13px] text-gray-500 p-6">Lade…</div>;
  if (!templates.length) {
    return (
      <div className="bg-white rounded-xl border border-black/[.07] p-8 text-center">
        <div className="text-[14px] text-gray-700 font-medium mb-1">Noch keine Vorlagen</div>
        <div className="text-[12.5px] text-gray-500">Klicke oben rechts auf „+ Neue Vorlage", um den ersten Agenten anzulegen.</div>
      </div>
    );
  }

  const onDelete = (tpl) => {
    if (confirm(`Vorlage „${tpl.display_name}" löschen?`)) del.mutate(tpl.id);
  };

  return (
    <div className="space-y-6">
      {groups.map(({ kind, roots, childrenByParent }) => (
        <section key={kind}>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-[10.5px] px-2 py-0.5 rounded-full ${KIND_COLORS[kind] || KIND_COLORS.custom}`}>
              {KIND_LABELS[kind] || kind}
            </span>
            <h2 className="text-[12.5px] font-medium text-gray-500 uppercase tracking-wider">
              {KIND_LABELS[kind] || kind}-Bereich
            </h2>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          <div className="space-y-2">
            {roots.map((parent) => {
              const children = childrenByParent[parent.id] || [];
              return (
                <div key={parent.id}>
                  <TemplateCard tpl={parent} onEdit={onEdit} onDelete={onDelete} />
                  {children.length > 0 && (
                    <div className="relative mt-1.5 ml-5 pl-5 border-l-2 border-gray-200 space-y-1.5">
                      {children.map((child) => (
                        <div key={child.id} className="relative">
                          <span className="absolute -left-5 top-5 w-5 h-px bg-gray-200" />
                          <TemplateCard tpl={child} onEdit={onEdit} onDelete={onDelete} compact />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function TemplateCard({ tpl, onEdit, onDelete, compact = false }) {
  return (
    <div
      className={`bg-white rounded-xl border border-black/[.07] shadow-sm ${
        compact ? 'p-3' : 'p-4'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-semibold text-[#0D3B50] ${compact ? 'text-[13.5px]' : 'text-[14.5px]'}`}>
              {tpl.display_name}
            </h3>
            {compact && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                Sub-Agent
              </span>
            )}
            {!tpl.is_active && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">inaktiv</span>
            )}
          </div>
          <div className="text-[11.5px] text-gray-400 mt-0.5">{tpl.slug}</div>
          {tpl.description && (
            <p className={`text-gray-600 mt-1.5 line-clamp-2 ${compact ? 'text-[12px]' : 'text-[12.5px]'}`}>
              {tpl.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2 text-[11.5px] text-gray-500">
            {tpl.model && !compact && <span>Modell: <code className="text-gray-700">{tpl.model}</code></span>}
            <span>{tpl.skills?.length || 0} Skill{(tpl.skills?.length || 0) === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          <button
            onClick={() => onEdit(tpl)}
            className="text-[12px] px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Bearbeiten
          </button>
          <button
            onClick={() => onDelete(tpl)}
            className="text-[12px] px-2.5 py-1 text-red-600 hover:bg-red-50 rounded-lg"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
}

function SkillsList({ onEdit }) {
  const { data, isLoading } = useAgentSkills();
  const del = useDeleteAgentSkill();
  const skills = data?.skills || [];

  if (isLoading) return <div className="text-[13px] text-gray-500 p-6">Lade…</div>;
  if (!skills.length) {
    return (
      <div className="bg-white rounded-xl border border-black/[.07] p-8 text-center">
        <div className="text-[14px] text-gray-700 font-medium mb-1">Noch keine Skills</div>
        <div className="text-[12.5px] text-gray-500">Skills sind wiederverwendbare Markdown-Bausteine, die du an Vorlagen anhängen kannst.</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-black/[.07] divide-y divide-gray-100">
      {skills.map((s) => (
        <div key={s.id} className="p-4 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-[14px] text-[#0D3B50]">{s.name}</h3>
              {!s.is_active && (
                <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">inaktiv</span>
              )}
            </div>
            <div className="text-[11.5px] text-gray-400">{s.slug}</div>
            {s.description && <p className="text-[12.5px] text-gray-600 mt-1">{s.description}</p>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(s)}
              className="text-[12px] px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Bearbeiten
            </button>
            <button
              onClick={() => { if (confirm(`Skill „${s.name}" löschen?`)) del.mutate(s.id); }}
              className="text-[12px] px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg"
            >
              Löschen
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TemplateEditor({ template, onClose }) {
  const isNew = !template?.id;
  const [form, setForm] = useState({
    display_name: template.display_name || '',
    slug: template.slug || '',
    kind: template.kind || 'custom',
    parent_template_id: template.parent_template_id || null,
    description: template.description || '',
    model: template.model || DEFAULT_MODEL,
    body_md: template.body_md || '',
    allowed_tools: template.allowed_tools || [],
    is_active: template.is_active !== false,
    skill_ids: (template.skills || []).map((s) => s.id),
  });
  const [error, setError] = useState(null);

  const { data: skillsData } = useAgentSkills({ active: true });
  const allSkills = skillsData?.skills || [];

  const { data: templatesData } = useAgentTemplates({ active: true });
  const parentCandidates = useMemo(() => {
    const all = templatesData?.templates || [];
    return all.filter((t) => {
      if (template?.id && t.id === template.id) return false;
      if (t.parent_template_id) return false;
      return t.kind === form.kind;
    });
  }, [templatesData, template?.id, form.kind]);

  const save = useSaveAgentTemplate();

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleTool = (t) => set('allowed_tools',
    form.allowed_tools.includes(t)
      ? form.allowed_tools.filter((x) => x !== t)
      : [...form.allowed_tools, t]
  );
  const toggleSkill = (id) => set('skill_ids',
    form.skill_ids.includes(id)
      ? form.skill_ids.filter((x) => x !== id)
      : [...form.skill_ids, id]
  );

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ id: template.id, ...form });
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="w-[min(720px,92vw)] max-h-[85vh] overflow-y-auto">
        <h2 className="text-[18px] font-semibold text-[#0D3B50] mb-4">
          {isNew ? 'Neue Agent-Vorlage' : `Vorlage „${template.display_name}"`}
        </h2>

        <div className="space-y-3">
          <Field label="Name">
            <input
              value={form.display_name}
              onChange={(e) => set('display_name', e.target.value)}
              placeholder="z.B. SEO-Spezialist"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Typ">
              <select
                value={form.kind}
                onChange={(e) => {
                  const k = e.target.value;
                  setForm((s) => ({ ...s, kind: k, parent_template_id: null }));
                }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
              >
                {Object.entries(KIND_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label="Slug (optional, wird sonst generiert)">
              <input
                value={form.slug}
                onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="seo-spezialist"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal font-mono"
              />
            </Field>
          </div>

          <Field label="Übergeordneter Agent (optional)">
            <select
              value={form.parent_template_id || ''}
              onChange={(e) => set('parent_template_id', e.target.value ? Number(e.target.value) : null)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
              disabled={parentCandidates.length === 0}
            >
              <option value="">— Eigenständiger Agent (Top-Level)</option>
              {parentCandidates.map((p) => (
                <option key={p.id} value={p.id}>{p.display_name}</option>
              ))}
            </select>
            <div className="text-[11px] text-gray-400 mt-1">
              {parentCandidates.length === 0
                ? `Kein Top-Level-${KIND_LABELS[form.kind] || form.kind}-Agent vorhanden, an den angedockt werden kann.`
                : 'Sub-Agent erscheinen eingerückt unter dem Parent in der Library.'}
            </div>
          </Field>

          <Field label="Kurzbeschreibung">
            <input
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Wofür ist dieser Agent gedacht?"
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
            />
          </Field>

          <Field label="Modell">
            <input
              value={form.model}
              onChange={(e) => set('model', e.target.value)}
              placeholder="claude-opus-4-7"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal font-mono"
            />
          </Field>

          <Field label="Erlaubte Tools">
            <div className="flex flex-wrap gap-1.5">
              {TOOL_PRESETS.map((t) => {
                const on = form.allowed_tools.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTool(t)}
                    className={`text-[11.5px] px-2.5 py-1 rounded-full border ${
                      on
                        ? 'bg-[#0D3B50] text-white border-[#0D3B50]'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={`Skills (${form.skill_ids.length} ausgewählt)`}>
            {allSkills.length === 0 ? (
              <div className="text-[12px] text-gray-400 italic py-2">
                Keine Skills vorhanden — leg im Skills-Tab welche an.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                {allSkills.map((s) => {
                  const on = form.skill_ids.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className={`flex items-start gap-2 px-3 py-2 text-[12.5px] cursor-pointer ${
                        on ? 'bg-teal-light/40' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleSkill(s.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{s.name}</div>
                        {s.description && (
                          <div className="text-[11.5px] text-gray-500 line-clamp-1">{s.description}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </Field>

          <Field label="System-Prompt / Body (Markdown)">
            <textarea
              value={form.body_md}
              onChange={(e) => set('body_md', e.target.value)}
              rows={10}
              placeholder={'# Rolle\nDu bist…\n\n## Vorgehen\n1. …\n2. …'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] font-mono outline-none focus:border-teal resize-y"
            />
          </Field>

          <label className="flex items-center gap-2 text-[12.5px] text-gray-700">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />
            Aktiv (in Auswahllisten verfügbar)
          </label>

          {error && (
            <div className="text-[12px] text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</div>
          )}
        </div>

        <div className="flex gap-2 mt-5 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={save.isPending || !form.display_name.trim()}
            className="ml-auto px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SkillEditor({ skill, onClose }) {
  const isNew = !skill?.id;
  const [form, setForm] = useState({
    name: skill.name || '',
    slug: skill.slug || '',
    description: skill.description || '',
    body_md: skill.body_md || '',
    is_active: skill.is_active !== false,
  });
  const [error, setError] = useState(null);
  const save = useSaveAgentSkill();
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ id: skill.id, ...form });
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="w-[min(600px,92vw)] max-h-[85vh] overflow-y-auto">
        <h2 className="text-[18px] font-semibold text-[#0D3B50] mb-4">
          {isNew ? 'Neuer Skill' : `Skill „${skill.name}"`}
        </h2>

        <div className="space-y-3">
          <Field label="Name">
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="z.B. SERP-Analyse"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
            />
          </Field>

          <Field label="Slug (optional)">
            <input
              value={form.slug}
              onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="serp-analyse"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal font-mono"
            />
          </Field>

          <Field label="Beschreibung">
            <input
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Was kann dieser Skill?"
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
            />
          </Field>

          <Field label="Body (Markdown)">
            <textarea
              value={form.body_md}
              onChange={(e) => set('body_md', e.target.value)}
              rows={12}
              placeholder={'## Vorgehen\n1. …\n\n## Tools\n- …'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] font-mono outline-none focus:border-teal resize-y"
            />
          </Field>

          <label className="flex items-center gap-2 text-[12.5px] text-gray-700">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />
            Aktiv
          </label>

          {error && (
            <div className="text-[12px] text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</div>
          )}
        </div>

        <div className="flex gap-2 mt-5 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={save.isPending || !form.name.trim()}
            className="ml-auto px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[12px] font-medium text-gray-700 block mb-1">{label}</label>
      {children}
    </div>
  );
}
